import { useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Send, UserX } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'

import type { Permisos } from '../api/capacidades'
import { cuenta } from '../api/cuenta'
import { ErrorDeLaApi, pedir } from '../api/nucleo'
import { Aviso, Boton } from '../componentes/ui'

/**
 * Lo propio de Usuarios sobre el editor genérico: dar de alta a alguien es **invitarle** —le
 * llega un correo con un enlace para poner su contraseña, y nadie tiene que inventársela— y,
 * en la ficha de otra persona, volver a mandarle ese enlace.
 */
export const AvisoDeInvitacion = () => (
  <Aviso tono="info">Le llegará un correo con un enlace para poner su contraseña (vale siete días). Tú no tienes que inventarle ninguna.</Aviso>
)

/** En la ficha: a otra persona, «Enviar enlace para entrar»; a uno mismo, su cuenta. */
export const AccionesDeUsuario = ({ espacio, id, permisos, yo }: { espacio: number; id: null | number; permisos: Permisos; yo: null | number }) => {
  const [estado, setEstado] = useState<{ enviando: boolean; mensaje: null | string; tono: 'error' | 'ok' }>({ enviando: false, mensaje: null, tono: 'ok' })
  const [revocando, setRevocando] = useState(false)
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  // Si todavía no ha aceptado la invitación: entonces se puede revocar.
  const { data: ficha } = useQuery({
    queryKey: ['usuario-pendiente', id],
    enabled: id !== null && id !== yo,
    queryFn: () => pedir<{ invitacionPendiente?: boolean; ultimaEntrada?: null | string }>(`/usuarios/${id}?depth=0`),
  })
  if (id === null) return null
  if (id === yo) {
    return (
      <Link to={`/e/${espacio}/cuenta`}>
        <Boton icono={<KeyRound />} pequeno tipo="discreto">
          Cambiar mi contraseña
        </Boton>
      </Link>
    )
  }
  // Mandar el enlace es de quien invita en la cuenta (el núcleo pide `usuarios.invitar`).
  if (!permisos.tiene('usuarios.invitar')) return null

  const mandar = async () => {
    setEstado({ enviando: true, mensaje: null, tono: 'ok' })
    try {
      const r = await cuenta.mandarEnlace(id)
      setEstado({ enviando: false, mensaje: r.mensaje, tono: 'ok' })
    } catch (e) {
      setEstado({ enviando: false, mensaje: e instanceof ErrorDeLaApi ? e.message : 'No hemos podido conectar. Vuelve a intentarlo.', tono: 'error' })
    }
  }

  const revocar = async () => {
    if (!window.confirm('Su enlace dejará de servir y, si solo era de esta cuenta, la persona se borra. ¿Revocar la invitación?')) return
    setRevocando(true)
    try {
      const r = await cuenta.revocar(id, espacio)
      await clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'usuarios'] })
      if (r.borrado) navegar(`/e/${espacio}/usuarios`, { replace: true })
      else setEstado({ enviando: false, mensaje: r.mensaje, tono: 'ok' })
    } catch (e) {
      setEstado({ enviando: false, mensaje: e instanceof ErrorDeLaApi ? e.message : 'No hemos podido conectar. Vuelve a intentarlo.', tono: 'error' })
    } finally {
      setRevocando(false)
    }
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      {ficha?.invitacionPendiente && (
        <Boton cargando={revocando} icono={<UserX />} onClick={() => void revocar()} pequeno tipo="discreto" title="Todavía no ha aceptado la invitación: su enlace deja de servir.">
          Revocar la invitación
        </Boton>
      )}
      <Boton cargando={estado.enviando} icono={<Send />} onClick={() => void mandar()} pequeno tipo="discreto" title="Le llega un correo para poner su contraseña: sirve si no abrió la invitación o la ha olvidado.">
        Enviar enlace para entrar
      </Boton>
      {estado.mensaje && (
        <span className={estado.tono === 'ok' ? 'text-xs text-ok' : 'text-xs text-error'} role={estado.tono === 'ok' ? 'status' : 'alert'}>
          {estado.mensaje}
        </span>
      )}
    </span>
  )
}
